import { Button, Input, Label, Spinner } from '@1trading/react';
import { useState } from 'react';
import { ReportFilesTable } from '@/features/reports/report-files-table';
import { useReports } from '@/hooks/queries/reports/use-reports';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage } from '@/lib/api/error';
import type { ReportsQuery } from '@/services/api/reports';
import { daysAgo, toDateInputValue } from '@/utils/date';

// Audit reports are generated ad hoc rather than daily, so default to a wider window than Reports.
const DEFAULT_RANGE_DAYS = 30;
const FETCH_ERROR_TOAST_ID = 'user-audit-fetch-failed';

const defaultQuery = (): ReportsQuery => ({
  type: 'account-audit',
  from: toDateInputValue(daysAgo(DEFAULT_RANGE_DAYS)),
  to: toDateInputValue(new Date()),
});

export const UserAuditReports = () => {
  const [draft, setDraft] = useState<ReportsQuery>(defaultQuery);
  // Auto-fetches once on mount with the defaults; afterwards only the Search
  // button moves `submitted`, so edits to the controls never fetch on their own.
  const [submitted, setSubmitted] = useState<ReportsQuery>(draft);

  const reportsQuery = useReports(submitted);
  const { data, isPending, isFetching, isError, error, refetch } = reportsQuery;

  useQueryErrorToast(reportsQuery, 'Failed to fetch user audit reports', FETCH_ERROR_TOAST_ID);

  const handleSearch = () => {
    if (draft.from === submitted.from && draft.to === submitted.to) {
      refetch();
      return;
    }
    setSubmitted(draft);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="user-audit-from">From</Label>
          <Input className="w-44" id="user-audit-from" onChange={(event) => setDraft((previous) => ({ ...previous, from: event.target.value }))} type="date" value={draft.from} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="user-audit-to">To</Label>
          <Input className="w-44" id="user-audit-to" onChange={(event) => setDraft((previous) => ({ ...previous, to: event.target.value }))} type="date" value={draft.to} />
        </div>
        <Button className="gap-2" disabled={isFetching} onClick={handleSearch} type="button">
          {isFetching ? <Spinner className="size-4" /> : null}
          Search
        </Button>
      </div>
      {isError ? (
        <div className="flex items-center gap-3">
          <p className="text-sm text-red-500">Failed to fetch user audit reports: {getErrorMessage(error)}</p>
          <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
            Retry
          </Button>
        </div>
      ) : null}
      <ReportFilesTable
        emptyMessage="No user audit reports found in the selected date range."
        files={data?.files}
        isPending={isPending}
        viewPathname="/operations/user-audit/view"
      />
    </div>
  );
};
UserAuditReports.displayName = 'UserAuditReports';
